import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Section } from "./Section";
import { LucideIcon } from "@/components/marketing/LucideIcon";
import { TECHNOLOGY } from "@/content/landing";

/* Animated node in the processing pipeline. */
function PipelineNode({ node, index, reduce }) {
  return (
    <motion.div
      data-testid={`tech-pipeline-node-${node.id}`}
      initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, ease: "easeOut", delay: index * 0.12 }}
      className="relative z-10 flex flex-col items-center rounded-2xl border border-border/60 bg-card/70 p-4 text-center backdrop-blur"
    >
      <span className="relative mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl text-white sema-cta-gradient shadow-lg shadow-sema-primary/30">
        <LucideIcon name={node.icon} className="h-5 w-5" />
        {!reduce && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-xl animate-pulse-glow"
            style={{ boxShadow: "0 0 0 3px rgba(91,120,229,0.25)" }}
          />
        )}
      </span>
      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-sema-mid">step {index + 1}</span>
      <div className="mt-1 font-display text-sm font-semibold tracking-tight text-foreground">{node.label}</div>
      <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{node.sub}</div>
    </motion.div>
  );
}

function Pipeline({ reduce }) {
  const { heading, nodes } = TECHNOLOGY.pipeline;
  return (
    <div className="mt-6">
      <h3 className="mb-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white/55">{heading}</h3>
      <div className="relative">
        {/* Animated flow line behind the row (lg only) */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 right-0 top-1/2 hidden h-px -translate-y-1/2 lg:block"
          style={{ background: "linear-gradient(90deg, transparent, rgba(91,120,229,0.45) 12%, rgba(15,174,150,0.45) 88%, transparent)" }}
        >
          {!reduce && (
            <motion.span
              className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full"
              style={{ background: "#0FAE96", boxShadow: "0 0 14px 3px rgba(15,174,150,0.7)" }}
              initial={{ left: "0%" }}
              animate={{ left: ["0%", "100%"] }}
              transition={{ duration: 4.5, ease: "easeInOut", repeat: Infinity }}
            />
          )}
        </div>
        <div className="relative grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {nodes.map((n, i) => (
            <PipelineNode key={n.id} node={n} index={i} reduce={reduce} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BeyondPillars({ reduce }) {
  const { kicker, heading, pillars } = TECHNOLOGY.beyond;
  return (
    <div className="mb-16">
      <div className="mb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sema-accent">{kicker}</p>
        <h3 className="mt-2 max-w-2xl font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {heading}
        </h3>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {pillars.map((p, i) => (
          <motion.div
            key={p.title}
            data-testid={`tech-beyond-pillar-${i}`}
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, ease: "easeOut", delay: i * 0.09 }}
            className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur transition-colors hover:border-sema-mid/50"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-14 -top-14 h-36 w-36 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-40"
              style={{ background: "radial-gradient(closest-side, #0FAE96, transparent)" }}
            />
            <span
              className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ backgroundColor: "rgba(15,174,150,0.14)", color: "#0FAE96" }}
            >
              <LucideIcon name={p.icon} className="h-5 w-5" />
            </span>
            <h4 className="relative mt-4 font-display text-lg font-semibold tracking-tight text-foreground">{p.title}</h4>
            <p className="relative mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            <p className="relative mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-sema-mid">{p.metric}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function StackLayers({ reduce }) {
  const { heading, note, layers } = TECHNOLOGY.stack;
  return (
    <div className="mt-16">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/55">{heading}</h3>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/70">{note}</p>
      <div className="mt-6 space-y-2.5">
        {layers.map((l, i) => (
          <motion.div
            key={l.name}
            data-testid={`tech-stack-layer-${i}`}
            initial={reduce ? { opacity: 1, x: 0 } : { opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, ease: "easeOut", delay: i * 0.07 }}
            className="group flex items-center gap-4 rounded-xl border border-white/10 bg-gradient-to-r from-sema-mid/20 to-transparent p-4 transition-colors hover:border-sema-mid/60 sm:gap-6"
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#050A18] font-mono text-xs font-bold text-sema-pale">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="flex flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-6">
              <div className="w-full font-display text-sm font-semibold tracking-tight text-white sm:w-64 sm:flex-shrink-0">
                {l.name}
              </div>
              <div className="text-xs leading-relaxed text-white/65">{l.detail}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function TechnologyShowcase() {
  const reduce = useReducedMotion();
  return (
    <section id="technology" data-testid="landing-technology" className="relative w-full border-t border-border/60">
      {/* ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{ background: "radial-gradient(48rem 24rem at 80% 0%, #5B78E5, transparent)" }}
      />
      <div className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28">
        <motion.header
          initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-14 max-w-3xl"
        >
          <div className="mb-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
            {TECHNOLOGY.eyebrow}
          </div>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Not a chat-with-your-data toy.{" "}
            <span className="sema-gradient-text">A governed analytics engine.</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{TECHNOLOGY.subtitle}</p>
        </motion.header>

        <BeyondPillars reduce={reduce} />

        {/* Technology pipeline panel */}
        <div className="sema-grain relative overflow-hidden rounded-3xl border border-white/10 bg-[#0B1228] p-6 backdrop-blur sm:p-10">
          <Pipeline reduce={reduce} />
          <StackLayers reduce={reduce} />
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href="#contact">
            <span
              data-testid="tech-cta"
              className="group inline-flex h-12 items-center rounded-full sema-cta-gradient px-6 font-mono text-xs uppercase tracking-[0.22em] text-white shadow-lg shadow-sema-primary/30 transition-transform hover:-translate-y-0.5"
            >
              See the engine on your data
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
